import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Surface.module.css'

interface SurfaceOwnProps {
  children?: ReactNode
  /** Measured panel padding. */
  pad?: 20 | 25 | 30
  /** Corner scale: the default panel, a larger card, or the largest shell. */
  radius?: 'panel' | 'card' | 'shell'
  /** Adds the hover lift and border brighten. */
  interactive?: boolean
  className?: string
}

type SurfaceProps<T extends ElementType> = SurfaceOwnProps & {
  as?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof SurfaceOwnProps | 'as'>

/**
 * The panel every card and container on the site is built from: a solid
 * surface with a hairline border.
 *
 * Polymorphic, so `as={Link}` gives a clickable card with no wrapper element.
 * `interactive` adds a lift on hover — cards deliberately carry no tilt or
 * cursor-tracked effects; those belong to buttons only.
 */
export const Surface = <T extends ElementType = 'div'>({
  children,
  as,
  pad,
  radius = 'panel',
  interactive = false,
  className,
  ...rest
}: SurfaceProps<T>) => {
  const Component = (as ?? 'div') as ElementType

  return (
    <Component
      {...rest}
      className={cn(
        styles.surface,
        interactive && 'raise',
        // Interactive surfaces catch the cursor's light; static panels are
        // not things you point at, so they stay unlit.
        interactive && 'torch',
        radius === 'card' && styles.card,
        radius === 'shell' && styles.shell,
        pad === 20 && styles.pad20,
        pad === 25 && styles.pad25,
        pad === 30 && styles.pad30,
        className,
      )}
    >
      {children}
    </Component>
  )
}
